import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/auth";
import { usePermissions } from "../context/PermissionContext";
import {
  TextField,
  Button,
  Container,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Paper,
} from "@mui/material";
import { jwtDecode } from "jwt-decode";
import logo from "../assets/Ralph_logo.png";

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const { login } = useAuth();
  const { updatePermissions } = usePermissions();
  const baseUrl = process.env.REACT_APP_BASE_URL;

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError("");

    // Basic validation
    if (!email || !password) {
      setError("Please enter both email and password");
      setLoading(false);
      return;
    }

    try {
      const response = await axios.post(`${baseUrl}/auth/login`, {
        email,
        password,
      });

      const { token, role, bookmark, permissions } = response.data;

      if (!token) {
        setError("Login failed: No authentication token received");
        setLoading(false);
        return;
      }

      // Store token and update auth context
      localStorage.setItem("token", token);
      login(token);

      // Store user role
      if (role) {
        localStorage.setItem("role", role);
        console.log("User role:", role);
      }

      // Decode token to get user information
      const decoded = jwtDecode(token);
      console.log("Login successful - User:", decoded.username);

      // Store user permissions
      if (permissions) {
        console.log("User permissions:", permissions);
        updatePermissions(permissions);
      } else {
        console.log("No permissions found in response");
        updatePermissions([]);
      }

      // Log bookmark details to console
      console.log("Bookmark object:", bookmark);
      if (bookmark) {
        console.log("Bookmark ID:", bookmark.id);
        console.log("Bookmark details:", {
          id: bookmark.id,
          customId: bookmark.customId,
          vehicleType: bookmark.vehicleType,
          ownerName: `${bookmark.ofirstName} ${bookmark.olastName}`,
          operatorName: `${bookmark.opfirstName} ${bookmark.oplastName}`,
        });
        // Store bookmark ID for limited-access users
        localStorage.setItem("bookmarkId", bookmark.id.toString());
      } else {
        console.log("No bookmark object found in response");
        // Clear bookmarkId if no bookmark
        localStorage.removeItem("bookmarkId");
      }

      // Handle navigation based on user permissions
      handlePermissionBasedNavigation(permissions, bookmark);
    } catch (error) {
      console.error("Login error:", error);
      handleLoginError(error);
    } finally {
      setLoading(false);
    }
  };

  const handlePermissionBasedNavigation = (permissions, bookmark) => {
    console.log(
      `Navigation - Permissions: ${permissions?.length || 0}, Bookmark ID: ${bookmark?.id || "None"}`,
    );

    // Check if user has admin-level permissions (user view permission)
    const hasAdminAccess = permissions?.some(p =>
      p.resource === 'user' && p.action === 'view'
    ) || false;

    if (!hasAdminAccess && bookmark && bookmark.id) {
      // Limited-access user with bookmark assignment
      console.log(
        `Redirecting limited-access user to bookmark profile: ${bookmark.id}`,
      );
      navigate(`/dashboard/bookmarkProfiles/${bookmark.id}`);
    } else if (!hasAdminAccess && !bookmark) {
      // Limited-access user without bookmark assignment
      console.warn("Limited-access user logged in but no vehicle assignment found");
      setError(
        "No vehicle assignment found. Please contact administrator.",
      );
      // Still navigate to dashboard
      navigate("/dashboard");
    } else {
      // Admin-level user or default case
      console.log("Redirecting to main dashboard");
      navigate("/dashboard");
    }
  };

  const handleLoginError = (error) => {
    if (error.response) {
      // Server responded with error status
      const status = error.response.status;
      const message =
        error.response.data?.error || error.response.data?.message;

      switch (status) {
        case 401:
          setError("Invalid email or password");
          break;
        case 403:
          setError("Account disabled. Please contact administrator.");
          break;
        case 404:
          setError("Account not found");
          break;
        case 422:
          setError("Invalid input data");
          break;
        case 500:
          setError("Server error. Please try again later.");
          break;
        default:
          setError(message || "Login failed. Please try again.");
          break;
      }
    } else if (error.request) {
      // Request made but no response received
      setError("Network error. Please check your connection and try again.");
    } else {
      // Other errors
      setError("An unexpected error occurred. Please try again.");
    }
  };

  const handleInputChange = (setter) => (e) => {
    setter(e.target.value);
    // Clear error when user starts typing
    if (error) setError("");
  };

  return (
    <Container
      component="main"
      maxWidth="xs"
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Paper
        elevation={8}
        sx={{
          padding: 4,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          width: "100%",
          borderRadius: 2,
        }}
      >
        {/* Logo */}
        <Box
          sx={{
            mb: 3,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <img
            src={logo}
            alt="Company Logo"
            style={{
              width: 200,
              height: "auto",
              marginBottom: 16,
            }}
          />
          <Typography
            component="h1"
            variant="h4"
            align="center"
            sx={{
              fontWeight: "bold",
              color: "primary.main",
            }}
          >
            Sign In
          </Typography>
          <Typography
            variant="body2"
            color="textSecondary"
            align="center"
            sx={{ mt: 1 }}
          >
            Enter your credentials to access your account
          </Typography>
        </Box>

        {/* Error Alert */}
        {error && (
          <Alert
            severity="error"
            sx={{
              width: "100%",
              mb: 2,
              "& .MuiAlert-message": {
                width: "100%",
              },
            }}
          >
            {error}
          </Alert>
        )}

        {/* Login Form */}
        <Box component="form" onSubmit={handleSubmit} sx={{ width: "100%" }}>
          <TextField
            fullWidth
            margin="normal"
            label="Email Address"
            variant="outlined"
            type="email"
            value={email}
            onChange={handleInputChange(setEmail)}
            required
            disabled={loading}
            autoComplete="email"
            autoFocus
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 1,
              },
            }}
          />
          <TextField
            fullWidth
            margin="normal"
            label="Password"
            variant="outlined"
            type="password"
            value={password}
            onChange={handleInputChange(setPassword)}
            required
            disabled={loading}
            autoComplete="current-password"
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 1,
              },
            }}
          />

          <Button
            type="submit"
            variant="contained"
            color="primary"
            fullWidth
            disabled={loading}
            sx={{
              mt: 3,
              mb: 2,
              py: 1.5,
              borderRadius: 1,
              textTransform: "none",
              fontSize: "1.1rem",
              fontWeight: "bold",
            }}
          >
            {loading ? (
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <CircularProgress size={20} sx={{ mr: 2 }} />
                Signing In...
              </Box>
            ) : (
              "Sign In"
            )}
          </Button>
        </Box>

        {/* Additional Information */}
        <Box sx={{ mt: 2, textAlign: "center" }}>
          <Typography variant="body2" color="textSecondary">
            Contact administrator for account assistance
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
};

export default Login;
